import { createContext, useMemo, useState } from 'react';

/**
 * Resolves the current language and base path from the browser location.
 *
 * Assumes that the last URL segment may contain a locale key (e.g. `/foo/bar/en`),
 * and that valid locales are the keys of `localeMap`. If the last segment matches
 * a supported locale, it is stripped from the path and returned as `locale`.
 * Otherwise, the `defaultLanguage` is used.
 *
 * Examples:
 *  - `/`                        → { pathName: '',            locale: defaultLanguage }
 *  - `/landing`                 → { pathName: '/landing',    locale: defaultLanguage }
 *  - `/landing/en`              → { pathName: '/landing',    locale: 'en' }
 *  - `/landing/some-page/es`    → { pathName: '/landing/some-page', locale: 'es' }
 *
 * @param {string} defaultLanguage - Fallback language code to use when no locale segment is found in the URL.
 * @param {{}} localeMap - Map of supported locale codes (the keys) to language attributes/values and site.webmaifest links.
 *   Only keys of this object are treated as valid URL locale segments.
 * @returns {{pathName: string, locale: string}}
 *   - `pathName`: the URL path without the trailing locale segment (if any),
 *   - `locale`: the resolved locale code.
 */
const getPathLocale = (defaultLanguage, localeMap) => {
    const segments = window.location.pathname.split('/').filter(Boolean);

    if (!segments.length) {
        return { pathName: '', locale: defaultLanguage };
    }

    const lastSegment = segments[segments.length - 1];
    const hasLocale = Object.keys(localeMap).includes(lastSegment);
    const baseSegments = hasLocale ? segments.slice(0, -1) : segments;

    const pathName = baseSegments.length ? `/${baseSegments.join('/')}` : '';
    const locale = hasLocale ? lastSegment : defaultLanguage;

    return { pathName, locale };
};

const LanguageContext = createContext(null);

/**
 * React provider that manages the current language and localized URLs for the app.
 * - Detect initial locale from the current path (via `getPathLocale`).
 * - Persist the selected language in storage under `storageKey` / `app.lng`.
 * - Expose `locale`, `path`, and `changeLanguage` via `LanguageContext`.
 * - Optionally redirect the browser when changing language or update the current
 *   URL and `<html lang>` / CookieYes document language in-place.
 *
 * @param {object} props - Component props.
 * @param {string} props.defaultLanguage - Language code served at the root path without a locale prefix.
 * @param {object} props.localeMap - Map from language code to its locale settings (for example `langCode`).
 * @param {import("react").ReactNode} props.children - Landing tree that reads the language state.
 * @returns {JSX.Element} - The context provider wrapping `children`.
 */
const LanguageProvider = ({ defaultLanguage, localeMap, children }) => {
    const [path, setPath] = useState(() => getPathLocale(defaultLanguage, localeMap));

    const changeLanguage = (newLanguage, redirect = true) => {
        const { pathName } = path;
        const { origin, search, hash } = window.location;
        const urlLocale = newLanguage === defaultLanguage ? '' : `/${newLanguage}/`;
        const urlPath = pathName ? `${pathName}${urlLocale ? '' : '/'}` : '';
        const newUrl = `${origin}${urlPath}${urlLocale}${search || ''}${hash || ''}`;

        if (redirect) {
            window.location.href = newUrl;
        } else {
            const langAttribute = localeMap[newLanguage]?.langCode;

            if (!langAttribute) return;

            document.documentElement.lang = langAttribute;
            window.ckySettings = {
                documentLang: langAttribute, // change language for CKY
            };

            setPath((prevPath) => ({
                ...prevPath,
                locale: newLanguage,
            }));

            window.history.pushState({}, '', newUrl);
        }
    };

    const contextValue = useMemo(() => {
        return { locale: path.locale, path: path.pathName, changeLanguage };
    }, [path.locale, path.pathName, changeLanguage]);

    return <LanguageContext value={contextValue}>{children}</LanguageContext>;
};

export { LanguageContext, LanguageProvider };
